/**
 * LoginTabController — 로그인 페이지 탭 전환 컨트롤러
 *
 * [구글 독립 프로젝트] 로그인 페이지에서 두 가지 로그인 방식을 탭으로 전환
 *
 * 탭 구조:
 *   탭 1 (구글): 기존 구글 OAuth 로그인 버튼
 *   탭 2 (앱 비밀번호): 이메일 + 앱 비밀번호 입력 폼
 *
 * 타겟:
 *   googleTab   — 구글 탭 버튼
 *   appTab      — 앱 비밀번호 탭 버튼
 *   googlePanel — 구글 로그인 패널 (컨텐츠 영역)
 *   appPanel    — 앱 비밀번호 로그인 패널 (컨텐츠 영역)
 *
 * 사이버펑크 컬러:
 *   활성 탭: var(--neon-primary) (네온 그린) 하단 보더 + 밝은 텍스트
 *   비활성 탭: var(--text-tertiary) dim 텍스트
 *
 * 사용법:
 *   data-controller="login-tab"
 *   data-action="click->login-tab#switchToGoogle"
 *   data-login-tab-target="googleTab"
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // 탭 버튼과 콘텐츠 패널 타겟 정의
  static targets = [
    "googleTab",   // 구글 로그인 탭 버튼
    "appTab",      // 앱 비밀번호 탭 버튼
    "googlePanel", // 구글 로그인 콘텐츠 패널
    "appPanel"     // 앱 비밀번호 로그인 콘텐츠 패널
  ]

  // ── 초기화 ──────────────────────────────────────────────
  connect() {
    // 기본 상태: 구글 탭 활성화
    this.showGoogleTab()
  }

  // ── 구글 탭으로 전환 ──────────────────────────────────
  // 클릭 이벤트: data-action="click->login-tab#switchToGoogle"
  switchToGoogle() {
    this.showGoogleTab()
  }

  // ── 앱 비밀번호 탭으로 전환 ──────────────────────────
  // 클릭 이벤트: data-action="click->login-tab#switchToApp"
  switchToApp() {
    this.showAppTab()
  }

  // ── 내부: 구글 탭 표시 ──────────────────────────────
  showGoogleTab() {
    // 구글 패널 표시, 앱 패널 숨김
    this.googlePanelTarget.style.display = "block"
    this.appPanelTarget.style.display    = "none"

    // 구글 탭 활성 스타일 (네온 그린 하단 보더)
    this.setActiveTab(this.googleTabTarget)
    this.setInactiveTab(this.appTabTarget)
  }

  // ── 내부: 앱 비밀번호 탭 표시 ────────────────────────
  showAppTab() {
    // 앱 패널 표시, 구글 패널 숨김
    this.googlePanelTarget.style.display = "none"
    this.appPanelTarget.style.display    = "block"

    // 앱 탭 활성 스타일 (네온 그린 하단 보더)
    this.setActiveTab(this.appTabTarget)
    this.setInactiveTab(this.googleTabTarget)
  }

  // ── 내부: 탭 활성 스타일 적용 ────────────────────────
  // 사이버펑크 네온 그린 (var(--neon-primary)) 하단 보더 + 밝은 텍스트
  setActiveTab(tab) {
    tab.style.borderBottom = "2px solid var(--neon-primary)"
    tab.style.color        = "var(--text-primary)"
    tab.style.opacity      = "1"
    tab.setAttribute("aria-selected", "true")
  }

  // ── 내부: 탭 비활성 스타일 적용 ─────────────────────
  // dim 처리 (var(--text-tertiary) 색상, 하단 보더 제거)
  setInactiveTab(tab) {
    tab.style.borderBottom = "2px solid transparent"
    tab.style.color        = "var(--text-tertiary)"
    tab.style.opacity      = "0.6"
    tab.setAttribute("aria-selected", "false")
  }
}
